import { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { AssimagraService } from "../../services/assimagra/assimagraService";
import { AssimagraMetrics, CompanyWithFootprint } from "../../types/assimagra/assimagra-metrics.types";

export const useAssimagraMetrics = () => {
  const [metrics, setMetrics] = useState<AssimagraMetrics | null>(null);
  const [companies, setCompanies] = useState<CompanyWithFootprint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const { user, claims, loading: authLoading } = useAuth();
  const mountedRef = useRef(true);

  const resetState = () => {
    if (mountedRef.current) {
      setMetrics(null);
      setCompanies([]);
      setError(null);
      setLoading(true);
      setRefreshing(false);
    }
  };

  const fetchMetrics = async (retryCount = 0) => {
    // Wait for auth to be ready and user to have assimagra or get2c role
    if (authLoading || !user?.uid || !claims?.role || (claims.role !== "assimagra" && claims.role !== "get2c")) {
      if (mountedRef.current) {
        setError("User not authenticated or not authorized");
        setLoading(false);
      }
      return;
    }

    try {
      if (mountedRef.current) {
        setLoading(true);
        setError(null);
      }

      // Check token freshness and refresh if needed
      const tokenResult = await user.getIdTokenResult();
      const isTokenExpiring = Date.now() > new Date(tokenResult.expirationTime).getTime() - 5 * 60 * 1000; // 5 minutes before expiry

      if (isTokenExpiring) {
        await user.getIdToken(true); // Force refresh
      }

      // First try to get existing metrics
      let metricsData = await AssimagraService.getMetrics(user.uid);

      // If no metrics exist, initialize them by calling refresh
      if (!metricsData && mountedRef.current) {
        metricsData = await AssimagraService.refreshMetrics(user.uid);
      }

      const companiesData = await AssimagraService.getCompaniesWithFootprints(user.uid);

      // Only update state if component is still mounted and user hasn't changed
      if (mountedRef.current && user?.uid) {
        setMetrics(metricsData);
        setCompanies(companiesData);
      }
    } catch (err) {
      // Retry logic for certain errors
      if (retryCount < 2 && mountedRef.current) {
        const shouldRetry =
          (err as any)?.code === "timeout" ||
          (err as any)?.code === "request-error" ||
          (err as any)?.message?.includes("Failed to create request");

        if (shouldRetry) {
          setTimeout(() => {
            if (mountedRef.current) {
              fetchMetrics(retryCount + 1);
            }
          }, 1000 * (retryCount + 1)); // Exponential backoff
          return;
        }
      }

      if (mountedRef.current) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
        setError(errorMessage);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  const refreshMetrics = async () => {
    if (authLoading || !user?.uid || !claims?.role || (claims.role !== "assimagra" && claims.role !== "get2c")) {
      if (mountedRef.current) {
        setError("User not authenticated or not authorized");
      }
      return;
    }

    try {
      if (mountedRef.current) {
        setRefreshing(true);
        setError(null);
      }

      const metricsData = await AssimagraService.refreshMetrics(user.uid);

      // Also refresh companies list
      const companiesData = await AssimagraService.getCompaniesWithFootprints(user.uid);

      // Only update state if component is still mounted and user hasn't changed
      if (mountedRef.current && user?.uid) {
        setMetrics(metricsData);
        setCompanies(companiesData);
      }
    } catch (err) {
      if (mountedRef.current) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
        setError(errorMessage);
      }
    } finally {
      if (mountedRef.current) {
        setRefreshing(false);
      }
    }
  };

  const autoRefreshMetrics = async () => {
    // Auto-refresh without showing loading state
    if (authLoading || !user?.uid || !claims?.role || (claims.role !== "assimagra" && claims.role !== "get2c")) {
      return;
    }

    try {
      const metricsData = await AssimagraService.refreshMetrics(user.uid);
      const companiesData = await AssimagraService.getCompaniesWithFootprints(user.uid);

      // Only update state if component is still mounted and user hasn't changed
      if (mountedRef.current && user?.uid) {
        setMetrics(metricsData);
        setCompanies(companiesData);
      }
    } catch (err) {
      console.error("Error auto-refreshing metrics:", err);
      // Don't show error to user for auto-refresh
    }
  };

  useEffect(() => {
    // Reset state when user changes (including logout)
    if (!user?.uid) {
      resetState();
      return;
    }

    // Only proceed if auth is ready and user has assimagra or get2c role
    if (!authLoading && user?.uid && (claims?.role === "assimagra" || claims?.role === "get2c")) {
      // Add a small delay to prevent rate limiting during rapid login cycles
      const timer = setTimeout(() => {
        if (mountedRef.current) {
          fetchMetrics(0);
        }
      }, 100);

      return () => clearTimeout(timer);
    } else if (!authLoading && (!user?.uid || (claims?.role !== "assimagra" && claims?.role !== "get2c"))) {
      // If auth is ready but user is not authenticated or not assimagra, stop loading
      if (mountedRef.current) {
        setLoading(false);
        setError("User not authenticated or not authorized");
      }
    }
  }, [user?.uid, claims?.role, authLoading]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    metrics,
    companies,
    loading,
    error,
    refreshing,
    refreshMetrics,
    autoRefreshMetrics,
    refetch: () => fetchMetrics(0),
  };
};
